import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { UsersRound } from "lucide-react";
import { 
  PieChart, 
  Pie, 
  Cell, 
  ResponsiveContainer, 
  Legend 
} from 'recharts';

interface ResourceAllocationChartProps {
  incidents: any[];
  catalogTasks: any[];
}

export function ResourceAllocationChart({ incidents, catalogTasks }: ResourceAllocationChartProps) {
  const allocationData = React.useMemo(() => {
    const assignmentGroups = new Map<string, { incidents: number, tasks: number, total: number }>();
    
    // Process incidents
    incidents.forEach(incident => {
      const group = incident.Service || "Unassigned";
      if (!assignmentGroups.has(group)) {
        assignmentGroups.set(group, { incidents: 0, tasks: 0, total: 0 });
      }
      
      const groupData = assignmentGroups.get(group)!;
      groupData.incidents += 1;
      groupData.total += 1;
    });
    
    // Process catalog tasks
    catalogTasks.forEach(task => {
      const group = task.Service || "Unassigned";
      if (!assignmentGroups.has(group)) {
        assignmentGroups.set(group, { incidents: 0, tasks: 0, total: 0 });
      }
      
      const groupData = assignmentGroups.get(group)!;
      groupData.tasks += 1;
      groupData.total += 1;
    });
    
    // Convert to array, sort by total, and take top 4 for cleaner visualization
    return Array.from(assignmentGroups.entries())
      .map(([name, data]) => ({ 
        name: name === "Unassigned" ? name : name.split(' ').slice(0, 2).join(' '), // Simplify names
        ...data,
        percentage: 0 // Will be calculated next
      }))
      .sort((a, b) => b.total - a.total)
      .slice(0, 4)
      .map(item => {
        const total = incidents.length + catalogTasks.length;
        return {
          ...item,
          percentage: Math.round((item.total / total) * 100)
        };
      });
  }, [incidents, catalogTasks]);

  // Simple purple palette
  const COLORS = ['#9b87f5', '#7E69AB', '#6E59A5', '#E5DEFF'];

  const totalTickets = allocationData.reduce((sum, item) => sum + item.total, 0);

  return (
    <Card className="hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base font-normal flex items-center gap-2">
          <UsersRound className="h-4 w-4 text-muted-foreground" />
          Resource Allocation
        </CardTitle>
        <Badge variant="secondary" className="font-normal">
          {totalTickets} tickets
        </Badge>
      </CardHeader>
      <CardContent>
        <div className="h-[300px] relative">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={allocationData}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={120}
                innerRadius={60}
                fill="#8884d8"
                dataKey="total"
                nameKey="name"
                label={({name, percentage}) => `${name}: ${percentage}%`}
              >
                {allocationData.map((_, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[index % COLORS.length]}
                    className="stroke-background hover:opacity-90 transition-opacity"
                  />
                ))}
              </Pie>
              <Legend
                verticalAlign="bottom"
                layout="horizontal"
                align="center"
                wrapperStyle={{
                  paddingTop: '20px',
                  fontSize: '12px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
